import { useState } from 'react';
import { cn } from '@/lib/cn';
import { saveFailureMessage } from '@/lib/apiErrors';
import { parseTypedAmount } from '@/lib/number';
import { pondAreaM2, type Crop, type PondFacts } from '@/lib/crop';
import { Dialog } from '@/components/ui/Dialog';
import { parseStockingText, stockingTextFrom, type StockingFieldKey, type StockingFieldText } from '@/lib/stockingFields';
import { headerActionClass } from './format';
import { StockingFields } from './StockingFields';
import { useStockingSave, type StockingChange } from './stockingData';

const MAX_STOCKED_PIECES = 2147483647;

/** Supervisors correct a stocked crop's details. Changing the stocked count needs a reason and is logged. */
export function StockingEditDialog({ farmId, pond, crop, onClose }: { farmId: string; pond: PondFacts; crop: Crop; onClose: () => void }) {
  const save = useStockingSave(farmId, crop.id);
  const area = pondAreaM2(pond.area_ha);
  const [pieces, setPieces] = useState(crop.stocking_count ? String(crop.stocking_count) : '');
  const [plStage, setPlStage] = useState(crop.stocking_details?.pl_stage ?? '');
  const [text, setText] = useState<StockingFieldText>(() => stockingTextFrom(crop.stocking_details));
  const [reason, setReason] = useState('');

  const parsed = parseTypedAmount(pieces, { integer: true });
  const count = 'value' in parsed ? parsed.value : null;
  const piecesProblem =
    'error' in parsed
      ? parsed.error
      : count === null
        ? 'Enter how many pieces were stocked.'
        : count <= 0 || count > MAX_STOCKED_PIECES
          ? 'Whole number above 0.'
          : null;
  const countChanged = count !== null && count !== crop.stocking_count;
  const reasonMissing = countChanged && !reason.trim();
  const { payload, errors } = parseStockingText(text);
  const valid = !piecesProblem && Object.keys(errors).length === 0 && !reasonMissing;

  const submit = () => {
    if (!valid || count === null) return;
    const body: StockingChange = {
      pieces_ordered: payload.pieces_ordered,
      ordered_allowance_pct: payload.ordered_allowance_pct,
      stocking_extras: payload.stocking_extras,
    };
    if (countChanged) body.stocking_count = count;
    if (plStage.trim() !== (crop.stocking_details?.pl_stage ?? '')) body.pl_stage = plStage.trim();
    if (countChanged) body.reason = reason.trim();
    save.mutate(body, { onSuccess: onClose });
  };

  const field = 'min-h-11 w-full rounded-lg border border-gray-300 px-3 text-base sm:text-sm';
  return (
    <Dialog open onClose={onClose} title={`Edit stocking details · ${pond.name}`} size="md">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
            Pieces stocked
            <input
              inputMode="numeric"
              aria-invalid={piecesProblem ? true : undefined}
              className={cn(field, 'tabular-nums', piecesProblem && 'border-red-500')}
              value={pieces}
              onChange={(e) => setPieces(e.target.value)}
            />
            {piecesProblem && <span className="text-xs font-normal text-red-700">{piecesProblem}</span>}
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
            PL stage
            <input className={field} value={plStage} maxLength={40} onChange={(e) => setPlStage(e.target.value)} placeholder="Optional" />
          </label>
        </div>
        <StockingFields
          text={text}
          onChange={(key: StockingFieldKey, value: string) => setText((t) => ({ ...t, [key]: value }))}
          count={count !== null && !piecesProblem ? count : null}
          areaM2={area}
          idPrefix="edit-stocking"
        />
        {countChanged && (
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-700">
            Reason for changing the count
            <input
              required
              className={cn(field, reasonMissing && 'border-red-500')}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. typo in the stocking sheet"
            />
            <span className="text-xs font-normal text-gray-500">Saved in the change log with who changed it, and from what.</span>
          </label>
        )}
        {save.isError && (
          <p role="alert" className="text-sm text-red-700">
            Not saved: {saveFailureMessage(save.error, 'try again')}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={cn(headerActionClass(), 'h-11')}>
            Cancel
          </button>
          <button type="submit" disabled={!valid || save.isPending} className={cn(headerActionClass(true), 'h-11 disabled:opacity-50')}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
